import {
  CarouselPaddingSizeMap,
  ResponsiveCardSizeMap,
} from 'omakase/HorizontalScrollContainer/HorizontalScrollContainer';
import { Theme, css } from '@emotion/react';

export const mainContainer =
  ({
    pageContainer,
    carouselPadding,
    verticalMargin,
  }: {
    pageContainer: boolean;
    carouselPadding: CarouselPaddingSizeMap;
    verticalMargin: number;
  }) =>
  (theme: Theme) => [
    pageContainer &&
      css`
        --carousel-padding: ${carouselPadding.xs}px;
        ${theme.breakpoints.up('sm')} {
          --carousel-padding: ${carouselPadding.sm}px;
        }
        margin: ${theme.spacing(verticalMargin)}px 0;
        ${theme.breakpoints.up('lg')} {
          // Commenting this out to allow component to take on container max-width
          /* max-width: 1280px; */
          margin-left: auto;
          margin-right: auto;
          padding-left: var(--carousel-padding);
          padding-right: var(--carousel-padding);
        }
      `,
    css`
      color: transparent;
      &:hover {
        color: #757575;
      }
    `,
  ];

export const headerOrFooter =
  ({ isHeader }: { isHeader: boolean }) =>
  (theme: Theme) => css`
    margin-bottom: ${theme.spacing(isHeader ? 2 : 0)}px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    ${theme.breakpoints.down('md')} {
      margin-left: var(--carousel-padding);
      margin-right: var(--carousel-padding);
    }
  `;

export const scrollContainer =
  (responsiveCardCount: Required<ResponsiveCardSizeMap>) => (theme: Theme) => css`
    --column-gap: ${theme.spacing(2)}px;
    --column-offset: ${theme.spacing(4)}px;
    --half-column-gap: calc(var(--column-gap) / 2);
    color: inherit;
    overflow-x: auto;
    display: flex;
    scroll-snap-type: x mandatory;
    padding-bottom: ${theme.spacing(0.5)}px;
    transition: color 250ms ease-in-out;
    ::-webkit-scrollbar {
      width: 14px;
      background-color: inherit;
    }
    ::-webkit-scrollbar-corner {
      background-color: transparent;
    }
    ::-webkit-scrollbar-thumb {
      background-clip: content-box;
      border: 6px solid transparent;
      border-radius: 10px;
      box-shadow: inset 0 0 0 10px;
    }
    ::-webkit-scrollbar-track {
      margin: 0 calc(var(--carousel-padding) - 6px);
    }
    ${theme.breakpoints.up('lg')} {
      --column-offset: 0px;
      ::-webkit-scrollbar-track {
        margin: 0 calc(var(--half-column-gap) * -0.8);
      }
    }
    ${theme.breakpoints.down('md')} {
      padding-inline-start: var(--carousel-padding);
      scroll-padding: 0 var(--carousel-padding);
    }

    > * {
      color: inherit;
    }

    li {
      position: relative;
      flex: 0 0 calc(100% / ${responsiveCardCount.xs} - var(--column-offset));
      margin: 0 0 0 var(--column-gap);
      scroll-snap-align: start;
      justify-content: center;
      align-items: center;
      padding: 0;
      &:first-of-type {
        margin-left: 0;
      }
      ${theme.breakpoints.up('sm')} {
        flex: 0 0 calc(100% / ${responsiveCardCount.sm} - var(--column-offset));
      }

      ${theme.breakpoints.up('md')} {
        flex: 0 0 calc(100% / ${responsiveCardCount.md} - var(--column-offset));
      }

      ${theme.breakpoints.up('lg')} {
        flex: 0 0
          calc(
            ((100% - var(--column-gap) * ${responsiveCardCount.lg - 1})) /
              ${responsiveCardCount.lg ?? 4}
          );
      }

      ${theme.breakpoints.up('lgx')} {
        flex: 0 0
          calc(
            ((100% - var(--column-gap) * ${responsiveCardCount.lgx - 1})) /
              ${responsiveCardCount.lgx ?? 4}
          );
      }

      ${theme.breakpoints.up('xl')} {
        flex: 0 0
          calc(
            ((100% - var(--column-gap) * ${responsiveCardCount.xl - 1})) /
              ${responsiveCardCount.xl ?? 4}
          );
      }

      &:last-of-type {
        margin-right: 0;
      }

      &.buffer {
        flex: 0 0 var(--carousel-padding);
        background-color: transparent;
        margin: 0;
        ${theme.breakpoints.up('lg')} {
          display: none;
          width: 0;
        }
      }
    }
  `;
